import { useState, useEffect, useMemo } from 'react';

import { DEFAULT_FILTERS } from '../../../../../hooks/useSearchFilters';
import { buildSearchParameters } from '../../../../../utils/buildSearchParameters';

/**
 * Reverse-maps API input format back to UI filter state.
 * Maps: searchTerms[] -> industrySearchTerms (comma-sep), ratingMin -> ratingFrom, etc.
 * HR: industrySearchTerms[] -> industrySearchTerms (comma-sep), revenueMin/Max -> /1_000_000, etc.
 */
const mapInputToFilters = (input, queryTypeCode) => {
	if (!input || typeof input !== 'object') return { ...DEFAULT_FILTERS };

	const isHR = queryTypeCode === 'b2bheroCommercialRegisterSearch';
	const filters = { ...DEFAULT_FILTERS };

	// Shared fields
	if (input.location) filters.location = input.location;
	if ((input.postalCodeFrom && input.postalCodeTo) || input.postalCodes) {
		filters.locationMode = 'plz';
		if (input.postalCodeFrom && input.postalCodeTo) {
			filters.plzFrom = input.postalCodeFrom;
			filters.plzTo = input.postalCodeTo;
		}
		if (input.postalCodes) filters.plzList = input.postalCodes;
	}
	if (input.country) filters.country = input.country;
	if (Array.isArray(input.states) && input.states.length > 0) {
		filters.states = [...input.states];
		if (filters.locationMode === 'place' && !input.location) filters.locationMode = 'state';
	}
	// Preserve the original query's radius exactly: if it had none, keep it
	// empty rather than inheriting the DEFAULT_FILTERS radius, so re-extending
	// a radius-less search doesn't silently gain a geo-radius.
	filters.radius = input.radius ?? null;
	if (input.maxResults) filters.maxResults = input.maxResults;
	if (input.websiteFilter !== undefined) filters.websiteFilter = input.websiteFilter;

	// Exclude keywords: array -> comma-separated string
	if (Array.isArray(input.excludeKeywords)) {
		filters.excludeKeywords = input.excludeKeywords.join(', ');
	}

	if (isHR) {
		// HR-specific reverse mapping. When industrySearchTerms are present alongside
		// industryCodes, the codes were auto-resolved from those terms — hydrate only
		// the terms so the WZ picker doesn't claim a manual selection.
		if (
			Array.isArray(input.industryCodes) &&
			input.industryCodes.length > 0 &&
			!input.industrySearchTerms?.length
		) {
			filters.industryCodes = [...input.industryCodes];
		}
		if (Array.isArray(input.industrySearchTerms)) {
			filters.industrySearchTerms = input.industrySearchTerms.join(', ');
		}
		if (Array.isArray(input.searchKeywords)) filters.searchKeywords = input.searchKeywords.join(', ');
		if (Array.isArray(input.legalForm)) filters.legalForm = input.legalForm;
		// Extending rebuilds the stored input from this filter state, so an exclusion that is not
		// hydrated here is dropped and its companies are fetched and charged again.
		if (Array.isArray(input.excludeProspectQueryIds)) {
			filters.excludeProspectQueryIds = [...input.excludeProspectQueryIds];
		}
		if (Array.isArray(input.excludeContactListIds)) {
			filters.excludeContactListIds = [...input.excludeContactListIds];
		}

		// Financial fields: API stores in EUR, UI shows in Mio
		if (input.revenueMin) filters.revenueFrom = String(input.revenueMin / 1_000_000);
		if (input.revenueMax) filters.revenueTo = String(input.revenueMax / 1_000_000);
		if (input.balanceTotalMin) filters.balanceTotalFrom = String(input.balanceTotalMin / 1_000_000);
		if (input.balanceTotalMax) filters.balanceTotalTo = String(input.balanceTotalMax / 1_000_000);
		if (input.earningsMin) filters.earningsFrom = String(input.earningsMin / 1_000_000);
		if (input.earningsMax) filters.earningsTo = String(input.earningsMax / 1_000_000);

		if (input.employeesMin) filters.employeesFrom = String(input.employeesMin);
		if (input.employeesMax) filters.employeesTo = String(input.employeesMax);

		if (input.includeEstimated !== undefined) filters.includeEstimated = input.includeEstimated;

		if (input.eventType) filters.eventType = input.eventType;
		if (input.eventDateFrom) filters.eventDateFrom = input.eventDateFrom;
		if (input.eventDateTo) filters.eventDateTo = input.eventDateTo;
	} else {
		// Maps-specific reverse mapping
		if (Array.isArray(input.searchTerms)) {
			filters.industrySearchTerms = input.searchTerms.join(', ');
		} else if (input.keyword) {
			filters.industrySearchTerms = input.keyword;
		}

		if (input.ratingMin) filters.ratingFrom = String(input.ratingMin);
		if (input.ratingMax) filters.ratingTo = String(input.ratingMax);
		if (input.minReviewCount) filters.minReviewCount = String(input.minReviewCount);
		if (input.skipClosedPlaces !== undefined) filters.includeClosedPlaces = !input.skipClosedPlaces;
		if (input.nameMatch === 'only_includes') filters.onlyNameMatches = true;
	}

	return filters;
};

/**
 * Deep-compare two filter states to detect changes.
 * Ignores maxResults since that is controlled by additionalResults in the modal.
 */
const filtersAreEqual = (a, b) => {
	for (const key of Object.keys(DEFAULT_FILTERS)) {
		if (key === 'maxResults') continue; // maxResults is handled separately via additionalResults
		const valA = a[key];
		const valB = b[key];

		if (Array.isArray(valA) && Array.isArray(valB)) {
			if (valA.length !== valB.length || valA.some((v, i) => v !== valB[i])) return false;
		} else if (valA !== valB) {
			return false;
		}
	}
	return true;
};

/**
 * Hook for managing search filters in the extend search modal.
 * Reverse-maps query.input to UI filter state, tracks changes, and builds modified parameters.
 *
 * @param {Object} queryInput - The query.input object from the prospect query
 * @param {string} queryTypeCode - The query type code (e.g. 'b2bheroLeadSearch')
 * @param {boolean} isOpen - Whether the modal is open (triggers reset)
 */
export const useExtendSearchFilters = (queryInput, queryTypeCode, isOpen) => {
	const originalFilters = useMemo(() => mapInputToFilters(queryInput, queryTypeCode), [queryInput, queryTypeCode]);

	const [filters, setFilters] = useState(originalFilters);

	// Reset when modal opens or query changes
	useEffect(() => {
		if (isOpen) {
			setFilters(mapInputToFilters(queryInput, queryTypeCode));
		}
	}, [isOpen, queryInput, queryTypeCode]);

	const isGermany = filters.country === 'Deutschland';
	const hasChanges = useMemo(() => !filtersAreEqual(filters, originalFilters), [filters, originalFilters]);

	const searchType = queryTypeCode === 'b2bheroCommercialRegisterSearch' ? 'hr' : 'maps';

	const handleFilterChange = (filterType, value) => {
		if (filterType === 'reset') {
			setFilters(originalFilters);
			return;
		}

		// PLZ mode and Bundesland only exist for Germany
		if (filterType === 'country' && value !== 'Deutschland') {
			setFilters((prev) => ({
				...prev,
				country: value,
				locationMode: 'place',
				plzFrom: '',
				plzTo: '',
				plzList: '',
				states: [],
			}));
			return;
		}

		// industryCodes is replaced wholesale (from WzCodePickerModal), not toggled
		if (filterType === 'industryCodes') {
			setFilters((prev) => ({ ...prev, industryCodes: Array.isArray(value) ? value : [] }));
			return;
		}

		// Check if it's a direct (non-array) field
		if (filterType in DEFAULT_FILTERS && !Array.isArray(DEFAULT_FILTERS[filterType])) {
			setFilters((prev) => ({ ...prev, [filterType]: value }));
			return;
		}

		// Toggle for array filters (legalForm)
		setFilters((prev) => {
			const currentValues = prev[filterType] || [];
			const newValues = currentValues.includes(value)
				? currentValues.filter((v) => v !== value)
				: [...currentValues, value];
			return { ...prev, [filterType]: newValues };
		});
	};

	/**
	 * Search parameters for the backend when the criteria changed, or null to keep the stored input.
	 * maxResults stays the original value: the stored input keeps it, the extension amount travels separately.
	 * @returns {object|null}
	 */
	const getModifiedSearchParameters = () => {
		if (!hasChanges) return null;

		const searchTerms = (filters.industrySearchTerms || '')
			.split(',')
			.map((x) => x.trim())
			.filter(Boolean);

		return buildSearchParameters(filters, searchType, searchTerms);
	};

	return {
		filters,
		searchType,
		isGermany,
		hasChanges,
		handleFilterChange,
		getModifiedSearchParameters,
	};
};
